// /reviews/$slug — individual customer review detail page with Review JSON-LD.
// Each review becomes a standalone, indexable URL so AEO engines (Google AI
// Overviews, Perplexity, ChatGPT, Bing Copilot) can cite specific testimonials
// with author + rating + service + date attribution.
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Star, Quote, ShieldCheck, ChevronLeft, Phone, MessageCircle, Mail, Calendar, MapPin, Tag } from "lucide-react";
import { getReview, REVIEWS } from "@/data/reviews";
import { speakableSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { LeadFormModal } from "@/components/LeadFormModal";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/reviews/$slug")({
  loader: ({ params }) => {
    const review = getReview(params.slug);
    if (!review) throw notFound();
    return { review };
  },
  head: ({ loaderData }) => {
    const r = loaderData?.review;
    if (!r) return {};
    const url = `${SITE}/reviews/${r.slug}`;
    const title = `${r.name} · ${r.rating}★ รีวิว ${r.service} | NYC Language Institute`;
    const desc = (r.body.length > 155 ? r.body.slice(0, 152) + "…" : r.body);
    return {
      meta: [
        { title: ctaTitle(title, "/reviews/$slug", "th") },
        { name: "description", content: ctaDesc(desc, "/reviews/$slug", "th")},
        { name: "robots", content: "index,follow,max-snippet:-1,max-image-preview:large" },
        { property: "og:title", content: ctaTitle(title, "/reviews/$slug", "th")},
        { property: "og:description", content: ctaDesc(desc, "/reviews/$slug", "th")},
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { property: "og:locale", content: r.language === "en" ? "en_US" : "th_TH" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/reviews/${r.slug}`),
      ],
      scripts: [
        speakableSchema(),
        // Round 12 — Review JSON-LD removed. Self-published reviews on a
        // business's own site are ineligible for review snippets and can
        // trigger a manual action. Visible testimonial content remains.
        {
          type: "application/ld+json" as const,
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: SITE },
              { "@type": "ListItem", position: 2, name: "Reviews", item: `${SITE}/reviews` },
              { "@type": "ListItem", position: 3, name: r.name, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: ReviewDetailPage,
  notFoundComponent: () => (
    <main className="flex min-h-screen items-center justify-center bg-navy-deep text-white">
      <div className="text-center">
        <h1 className="text-2xl font-bold">ไม่พบรีวิวนี้</h1>
        <Link to="/reviews" className="mt-4 inline-block text-amber-300 underline">ดูรีวิวทั้งหมด</Link>
      </div>
    </main>
  ),
});

function ReviewDetailPage() {
  const { review: r } = Route.useLoaderData();
  const related = REVIEWS.filter((x) => x.slug !== r.slug).slice(0, 3);

  return (
    <main className="min-h-screen bg-navy-deep text-white" data-speakable="true">
      <section className="border-b border-white/10 bg-gradient-to-b from-[#0a1a3d] to-[#0d2150] px-4 py-10">
        <div className="mx-auto max-w-3xl">
          <Link to="/reviews" className="inline-flex items-center gap-1 text-sm text-amber-300 hover:text-amber-200">
            <ChevronLeft className="h-4 w-4" /> รีวิวทั้งหมด
          </Link>
          <div className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-amber-400/40 bg-amber-500/10 px-3 py-1 text-xs font-semibold text-amber-200">
            <ShieldCheck className="h-3.5 w-3.5" /> Verified Customer Review
          </div>
          <h1 className="mt-3 text-2xl font-extrabold sm:text-4xl">
            {r.name} <span className="text-amber-300">· {r.rating}/5 ดาว</span>
          </h1>
          <p className="mt-2 text-sm text-white/80">{r.role}</p>
          <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-white/70">
            <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {r.date}</span>
            {r.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {r.location}</span>}
            <span className="inline-flex items-center gap-1"><Tag className="h-3.5 w-3.5" /> {r.service}</span>
          </div>
        </div>
      </section>

      <section className="px-4 py-10">
        <article className="mx-auto max-w-3xl rounded-3xl border border-white/10 bg-white/[0.04] p-7 sm:p-10">
          <div className="flex items-center gap-1 text-amber-300">
            {[...Array(r.rating)].map((_, i) => <Star key={i} className="h-5 w-5 fill-amber-300" />)}
          </div>
          <Quote className="mt-4 h-10 w-10 text-amber-300/20" />
          <p className="mt-2 text-lg leading-relaxed text-white/90 quick-answer" data-speakable="true">
            "{r.bodyLong || r.body}"
          </p>
          <div className="mt-6 border-t border-white/10 pt-5">
            <p className="text-base font-bold text-white">— {r.name}</p>
            <p className="text-sm text-white/70">{r.role}</p>
            {r.serviceUrl && (
              <p className="mt-3 text-sm">
                <span className="text-white/60">บริการที่ใช้:</span>{" "}
                <Link to={r.serviceUrl} className="font-semibold text-amber-300 underline hover:text-amber-200">
                  {r.service}
                </Link>
              </p>
            )}
          </div>
        </article>
      </section>

      <section className="px-4 pb-10">
        <div className="mx-auto max-w-3xl rounded-3xl border border-amber-400/30 bg-gradient-to-br from-amber-500/10 to-amber-500/5 p-7 text-center">
          <h2 className="text-xl font-extrabold">อยากเป็นรีวิวที่ดีรายต่อไป?</h2>
          <p className="mt-2 text-sm text-white/80">ขอใบเสนอราคาภายใน 15 นาที — ไม่ผูกมัด</p>
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            <a href="tel:0832494999" className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-3 text-sm font-bold text-navy-deep hover:bg-amber-400">
              <Phone className="h-4 w-4" /> 083-249-4999
            </a>
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-xl bg-[#04A03A] px-5 py-3 text-sm font-bold text-white hover:bg-[#05B14C]">
              <MessageCircle className="h-4 w-4" /> LINE @nycli
            </a>
            <LeadFormModal trigger={
              <button type="button" className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/5 px-5 py-3 text-sm font-bold text-white hover:bg-white/10">
                <Mail className="h-4 w-4" /> ขอใบเสนอราคา
              </button>
            } />
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 px-4 py-10">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-lg font-extrabold">รีวิวอื่นที่เกี่ยวข้อง</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            {related.map((x) => (
              <Link
                key={x.slug}
                to="/reviews/$slug"
                params={{ slug: x.slug }}
                className="block rounded-2xl border border-white/10 bg-white/[0.04] p-5 transition hover:border-amber-400/30"
              >
                <div className="flex items-center gap-1 text-amber-300">
                  {[...Array(x.rating)].map((_, i) => <Star key={i} className="h-3 w-3 fill-amber-300" />)}
                </div>
                <p className="mt-2 line-clamp-3 text-sm text-white/85">"{x.body}"</p>
                <p className="mt-3 text-xs font-bold text-white">{x.name}</p>
                <p className="text-[11px] text-amber-300/80">{x.service}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
